import React, { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import TemplateRenderer from "../components/templates/TemplateRenderer";

/* Mock data mirrors TemplatePreviewModal.buildMockPage so the standalone
   preview page shows the same realistic content the modal used to show. */
const TEMPLATE_META = {
  neon_pulse:   { name: "Créatif",     accent: "#22D3EE" },
  aurora:       { name: "Agence",      accent: "#7C3AED" },
  glass_premium:{ name: "Bureautique", accent: "#0EA5E9" },
  cyber_grid:   { name: "Cyber",       accent: "#22C55E" },
  holographic:  { name: "Holographic", accent: "#EC4899" },
  minimal_lux:  { name: "Minimal",     accent: "#1F2937" },
  bento:        { name: "Freelance",   accent: "#2563EB" },
  gastronomy:   { name: "Gastronomie", accent: "#B45309" },
  atelier_wood: { name: "Artisan",     accent: "#92400E" },
  salon_velvet: { name: "Salon",       accent: "#C2185B" },
  boutique_drop:{ name: "Boutique",    accent: "#FB923C" },
  medtech:      { name: "Cabinet",     accent: "#10B981" },
};

const buildMockPage = (templateId, name, accent) => ({
  template: templateId,
  business_name: name,
  activity: "Aperçu de démonstration",
  description:
    "Aperçu interactif de ce modèle. Vous pourrez personnaliser chaque section, couleurs et contenus depuis votre espace client après votre achat.",
  primary_color: accent,
  secondary_color: "#F0ABFC",
  logo_url: null,
  banner_url: "https://images.unsplash.com/photo-1497366754035-f200968a6e72?q=80&w=1600",
  phone: "+33 1 23 45 67 89",
  email: "contact@exemple.fr",
  address: "12 rue de la Paix, 75002 Paris",
  siret: "812 345 678 00012",
  cta_text: "Prendre contact",
  cta_url: "#contact",
  social_links: { facebook: "#", instagram: "#", linkedin: "#" },
  opening_hours: [
    { day: "Lundi",    open: "09:00", close: "18:00", closed: false },
    { day: "Mardi",    open: "09:00", close: "18:00", closed: false },
    { day: "Mercredi", open: "09:00", close: "18:00", closed: false },
    { day: "Jeudi",    open: "09:00", close: "18:00", closed: false },
    { day: "Vendredi", open: "09:00", close: "18:00", closed: false },
    { day: "Samedi",   open: "10:00", close: "17:00", closed: false },
    { day: "Dimanche", open: "",      close: "",      closed: true  },
  ],
  services: [
    { title: "Prestation phare",       description: "Description claire de votre service principal.", price: "À partir de 120€" },
    { title: "Service complémentaire", description: "Un second service présenté avec finesse.",       price: "Sur devis" },
    { title: "Offre signature",        description: "Une signature qui démarque votre marque.",       price: "Premium" },
  ],
  testimonials: [
    { author: "Camille L.", role: "Cliente fidèle", text: "Un service exceptionnel, je recommande les yeux fermés.", rating: 5 },
    { author: "Antoine R.", role: "Client",          text: "Très professionnel, à l’écoute et résultats au rendez-vous.", rating: 5 },
  ],
  gallery: [
    "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=800",
    "https://images.unsplash.com/photo-1503602642458-232111445657?q=80&w=800",
    "https://images.unsplash.com/photo-1521146764736-56c929d59c83?q=80&w=800",
    "https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?q=80&w=800",
  ],
  show_map: false,
  map_query: "",
});

const TemplatePreviewPage = () => {
  const { templateId } = useParams();
  const meta = TEMPLATE_META[templateId] || { name: "Modèle", accent: "#3B82F6" };
  const page = useMemo(() => buildMockPage(templateId, meta.name, meta.accent), [templateId, meta.name, meta.accent]);

  useEffect(() => {
    document.title = `Aperçu — ${meta.name} · Le Guide des Entrepreneurs`;
  }, [meta.name]);

  return (
    <div className="min-h-screen bg-white font-preserve" data-testid="template-preview-page">
      {/* Ribbon banner across the top so visitors know they are in a demo */}
      <div className="sticky top-0 z-50 bg-blue-600 text-white text-xs sm:text-sm font-semibold px-4 py-2.5 flex items-center justify-center flex-wrap gap-x-3 gap-y-1 shadow-md">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-300 animate-pulse" />
          <span>Aperçu du modèle « {meta.name} » — démo avec données fictives</span>
        </div>
        <a
          href="/modeles"
          className="underline underline-offset-2 hover:text-white/90"
          data-testid="preview-page-cta"
        >
          Revenir sur Le Guide des Entrepreneurs.fr
        </a>
      </div>
      <TemplateRenderer page={page} />
    </div>
  );
};

export default TemplatePreviewPage;
